/**
 * Group classification — the foundation every report stands on.
 *
 * SIGN CONVENTION, once, for the whole web layer: negative = debit, positive =
 * credit, exactly as normalise.ts produced it. Nothing below flips a sign
 * (CLAUDE.md §14.7). Screens convert to "amount owed" or "balance in hand" at
 * the point of display by negating a debit, and that is a presentation choice,
 * not a change of convention.
 */
import type { Group, Ledger, Snapshot } from "@tally/shared";

/** Tally's reserved primary groups, matched case-insensitively. */
export const PRIMARY = {
  capital: "capital account",
  loans: "loans (liability)",
  currentLiabilities: "current liabilities",
  fixedAssets: "fixed assets",
  investments: "investments",
  currentAssets: "current assets",
  branches: "branch / divisions",
  miscExpenses: "misc. expenses (asset)",
  suspense: "suspense a/c",
  sales: "sales accounts",
  purchase: "purchase accounts",
  directExpenses: "direct expenses",
  indirectExpenses: "indirect expenses",
  directIncomes: "direct incomes",
  indirectIncomes: "indirect incomes",
} as const;

export type GroupIndex = {
  byName: Map<string, Group>;
  /** ledger name to the chain of group names from its own group up to primary */
  ancestryOf: (ledgerName: string) => string[];
  primaryOf: (ledgerName: string) => string;
  isRevenue: (ledgerName: string) => boolean;
  /** true when any group in the ledger's ancestry matches */
  under: (ledgerName: string, ...groupNames: string[]) => boolean;
};

export function indexGroups(snapshot: Snapshot): GroupIndex {
  const byName = new Map<string, Group>();
  for (const group of snapshot.groups) byName.set(group.name.toLowerCase(), group);

  const ledgerParent = new Map<string, string>();
  for (const ledger of snapshot.ledgers) {
    ledgerParent.set(ledger.name.toLowerCase(), ledger.parent);
  }

  const ancestryCache = new Map<string, string[]>();

  function ancestryOfGroup(groupName: string): string[] {
    const chain: string[] = [];
    const seen = new Set<string>();
    let current: string | null = groupName;

    // A malformed export can contain a parent cycle. Walking it would hang the
    // phone rather than fail a report, so cap it explicitly.
    while (current && !seen.has(current.toLowerCase())) {
      seen.add(current.toLowerCase());
      chain.push(current);
      const group: Group | undefined = byName.get(current.toLowerCase());
      if (!group) break;
      current = group.parent;
    }
    return chain;
  }

  function ancestryOf(ledgerName: string): string[] {
    const key = ledgerName.toLowerCase();
    const cached = ancestryCache.get(key);
    if (cached) return cached;

    const parent = ledgerParent.get(key);
    const chain = parent ? ancestryOfGroup(parent) : [];

    // primaryGroup is Tally's own answer and beats walking the chain, which can
    // stop early when the master export missed an intermediate group.
    const own = parent ? byName.get(parent.toLowerCase()) : undefined;
    if (own?.primaryGroup && !chain.some((n) => n.toLowerCase() === own.primaryGroup.toLowerCase())) {
      chain.push(own.primaryGroup);
    }

    ancestryCache.set(key, chain);
    return chain;
  }

  function primaryOf(ledgerName: string): string {
    const chain = ancestryOf(ledgerName);
    return chain[chain.length - 1] ?? "";
  }

  function isRevenue(ledgerName: string): boolean {
    const chain = ancestryOf(ledgerName);
    for (const name of chain) {
      const group = byName.get(name.toLowerCase());
      if (group?.isRevenue) return true;
    }
    const primary = primaryOf(ledgerName).toLowerCase();
    return (
      primary === PRIMARY.sales ||
      primary === PRIMARY.purchase ||
      primary === PRIMARY.directExpenses ||
      primary === PRIMARY.indirectExpenses ||
      primary === PRIMARY.directIncomes ||
      primary === PRIMARY.indirectIncomes
    );
  }

  function under(ledgerName: string, ...groupNames: string[]): boolean {
    const wanted = groupNames.map((n) => n.toLowerCase());
    return ancestryOf(ledgerName).some((name) => wanted.includes(name.toLowerCase()));
  }

  return { byName, ancestryOf, primaryOf, isRevenue, under };
}

/* --------------------------------------------------- well-known buckets --- */

const CASH_GROUPS = ["cash-in-hand", "cash in hand"];
const BANK_GROUPS = ["bank accounts", "bank od a/c", "bank occ a/c", "bank od account"];

export function isCash(index: GroupIndex, ledger: Ledger): boolean {
  return index.under(ledger.name, ...CASH_GROUPS);
}

export function isBank(index: GroupIndex, ledger: Ledger): boolean {
  return index.under(ledger.name, ...BANK_GROUPS);
}

export function isReceivable(index: GroupIndex, ledger: Ledger): boolean {
  return index.under(ledger.name, "sundry debtors");
}

export function isPayable(index: GroupIndex, ledger: Ledger): boolean {
  return index.under(ledger.name, "sundry creditors");
}

export function isSalesLedger(index: GroupIndex, ledger: Ledger): boolean {
  return index.under(ledger.name, "sales accounts");
}

/** Round to paisa. Float dust accumulated over thousands of entries is visible. */
export function money(n: number): number {
  return Math.round(n * 100) / 100;
}

export function sum(values: number[]): number {
  return money(values.reduce((total, n) => total + n, 0));
}
